import { useState } from 'react';
import type { IconType } from 'react-icons';
import { IoCreateOutline } from 'react-icons/io5';

import { Avatar } from '@/components/Avatar/Avatar';
import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { GlassButton } from '@/components/GlassButton';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import type { MappedPersonForSelection } from '@/store/persons/person.dto';

import type { SignatureState } from '../types';

import { SectionHeader } from './SectionHeader';
import s from './SignaturesSection.module.scss';
import { UserPickerModal } from './UserPickerModal';

interface SignaturesSectionProps {
  captain: SignatureState;
  officer: SignatureState;
  captainUser: MappedPersonForSelection | null;
  officerUser: MappedPersonForSelection | null;
  officerEligibleUsers: MappedPersonForSelection[];
  canAssignOfficer: boolean;
  isOfficerLocked: boolean;
  canSignOfficerAsCurrentUser: boolean;
  onAssignOfficer: (personId: string) => void;
  onSignCaptain: () => void;
  onSignOfficer: () => void;
  icon?: IconType;
}

interface CaptainBoxProps {
  sig: SignatureState;
  user: MappedPersonForSelection | null;
  onSign: () => void;
  canSign: boolean;
}

function formatSignatureTime(isoString: string) {
  const date = new Date(isoString);
  const dateStr = date.toLocaleDateString('en-GB');
  const timeStr = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return `${dateStr} · ${timeStr}`;
}

function SignedView({
  user,
  signedAt,
}: {
  user: MappedPersonForSelection | null;
  signedAt: string;
}) {
  return (
    <div className={s.signed}>
      <div className={s.checkmark}>✓</div>
      <div>
        <div className={s.signedBy}>{user ? `${user.firstName} ${user.lastName}` : ''}</div>
        <div className={s.signedAt}>{formatSignatureTime(signedAt)}</div>
      </div>
    </div>
  );
}

function UserRow({ user }: { user: MappedPersonForSelection }) {
  return (
    <>
      <Avatar firstName={user.firstName} lastName={user.lastName} src={user.avatar} size={28} />
      <div className={s.info}>
        <div className={s.name}>
          {user.firstName} {user.lastName}
        </div>
      </div>
    </>
  );
}

function CaptainBox({ sig, user, onSign, canSign }: CaptainBoxProps) {
  return (
    <div className={s.signatureBox}>
      <div className={s.title}>Captain</div>
      {sig.signedAt ? (
        <SignedView user={user} signedAt={sig.signedAt} />
      ) : (
        <div className={s.unsigned}>
          {user && <UserRow user={user} />}
          {canSign && <ButtonSmall onClick={onSign}>Sign & Save</ButtonSmall>}
        </div>
      )}
    </div>
  );
}

interface OfficerBoxProps {
  sig: SignatureState;
  user: MappedPersonForSelection | null;
  eligibleUsers: MappedPersonForSelection[];
  canAssign: boolean;
  isLocked: boolean;
  canSignAsCurrentUser: boolean;
  canSignPermission: boolean;
  onAssign: (personId: string) => void;
  onSign: () => void;
}

function OfficerBox({
  sig,
  user,
  eligibleUsers,
  canAssign,
  isLocked,
  canSignAsCurrentUser,
  canSignPermission,
  onAssign,
  onSign,
}: OfficerBoxProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const canShowChange = canAssign && !isLocked;
  const canShowSign = canSignAsCurrentUser && canSignPermission;

  return (
    <div className={s.signatureBox}>
      <div className={s.title}>Crew member</div>
      {sig.signedAt ? (
        <SignedView user={user} signedAt={sig.signedAt} />
      ) : user ? (
        <div className={s.unsigned}>
          <UserRow user={user} />
          {canShowChange && (
            <GlassButton size={28} className={s.changeBtn} onClick={() => setPickerOpen(true)}>
              Change
            </GlassButton>
          )}
          {canShowSign && <ButtonSmall onClick={onSign}>Sign & Save</ButtonSmall>}
        </div>
      ) : (
        <div className={s.unsigned}>
          <ButtonOutlinedSmall
            onClick={() => setPickerOpen(true)}
            disabled={!canAssign}
            title={!canAssign ? 'Only the captain can assign a crew member.' : undefined}
          >
            Assign Crew Member
          </ButtonOutlinedSmall>
        </div>
      )}

      <UserPickerModal
        open={pickerOpen}
        title="Assign Crew Member"
        users={eligibleUsers}
        disabledIds={new Set()}
        onSelectUser={(u) => onAssign(u.id)}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}

export function SignaturesSection({
  captain,
  officer,
  captainUser,
  officerUser,
  officerEligibleUsers,
  canAssignOfficer,
  isOfficerLocked,
  canSignOfficerAsCurrentUser,
  onAssignOfficer,
  onSignCaptain,
  onSignOfficer,
  icon: Icon = IoCreateOutline,
}: SignaturesSectionProps) {
  const { can } = usePermissions();
  const canSignCaptain = can(PERMISSIONS.logbook.DailyLogSignCaptain);
  const canSignOfficer = can(PERMISSIONS.logbook.DailyLogSignMate);
  return (
    <div className={s.section}>
      <SectionHeader icon={Icon} title="Signatures" />
      <div className={s.grid}>
        <CaptainBox
          sig={captain}
          user={captainUser}
          onSign={onSignCaptain}
          canSign={canSignCaptain}
        />
        <OfficerBox
          sig={officer}
          user={officerUser}
          eligibleUsers={officerEligibleUsers}
          canAssign={canAssignOfficer}
          isLocked={isOfficerLocked}
          canSignAsCurrentUser={canSignOfficerAsCurrentUser}
          canSignPermission={canSignOfficer}
          onAssign={onAssignOfficer}
          onSign={onSignOfficer}
        />
      </div>
    </div>
  );
}
